/**
 * SpacetimeDB connection — builds the typed DbConnection and persists the auth
 * token so a reload resumes the same identity (the Atlas Passport is durable).
 */

import { DbConnection, type ErrorContext } from '../module_bindings/index.js';
import { installFastFind } from '../lib/fast_find.js';
import { watchReducers } from '../lib/session_watch.js';
import { bus } from '../bus.js';
import type { Identity } from 'spacetimedb';
import { STDB_WS_URI, STDB_DB_NAME } from '../config.js';
import { getToken, setToken } from '../lib/auth_store.js';
import { openStdbSocket, stdbSocketMode } from './stdb_socket.js';

export interface ConnectionCallbacks {
  onConnect: (conn: DbConnection, identity: Identity, token: string) => void;
  onConnectError?: (err: Error) => void;
  onDisconnect?: (err?: Error) => void;
}

/**
 * Build and initiate a SpacetimeDB connection. The returned object is not yet
 * connected — `onConnect` fires once the handshake completes. `token` is the session the sign-in resolved; reading
 * storage again could pick up another tab's token, or throw where storage is blocked (final review M2).
 */
/** The session token the live connection stored at connect — a refusal must not clear a NEWER one another tab saved. */
let connectedToken: string | null = null;
export function tokenAtConnect(): string | null { return connectedToken; }

export function connect(cb: ConnectionCallbacks, token?: string): DbConnection {
  const savedToken = token ?? getToken() ?? undefined;

  const builder = DbConnection.builder()
    .withUri(STDB_WS_URI)
    .withDatabaseName(STDB_DB_NAME)
    .withToken(savedToken)
    .onConnect((conn, identity, token) => {
      // Persist the token so the next session resumes this identity (guarded: blocked storage must not throw here).
      setToken(token);
      connectedToken = token;
      cb.onConnect(conn, identity, token);
    })
    .onConnectError((_ctx: ErrorContext, err: Error) => {
      cb.onConnectError?.(err);
    })
    .onDisconnect((_ctx: ErrorContext, err?: Error) => {
      cb.onDisconnect?.(err);
    });
  // THE SOCKET WORKER (2026-10-05): a page frozen > 30 s (a heavy boot frame at high settings) stopped draining the
  // socket, SpacetimeDB timed the client out and the reconnect law reloaded the page — the loading screen started over.
  // The socket now lives in a worker that keeps reading it (net/stdb_socket.ts). 'atlas:ws-worker' = 'off' = the SDK's.
  if (stdbSocketMode() === 'worker') builder.withWSFn(openStdbSocket);
  const conn = builder.build();
  // REAL INDEXES (2026-09-20): the SDK's `find` / `filter` accessors scan the whole table per call (1.9 ms on the
  // 11.7k-row versona table); 180 call sites assume they are lookups. Patched once, here, before any module reads.
  installFastFind(conn);
  // A guarded reducer refused on a stale session (signed in elsewhere, password changed): send the player to Sign in.
  watchReducers(conn.reducers, () => bus.emit('atlas:session-ended', {}));
  return conn;
}
